import { useCallback } from 'react';
import { type ZodError } from 'zod';

import type { RxDraft, RxKind, RxRequest } from '@/entities/rx';
import { createEmptyRxDraft, rxDraftSchema, rxRequestFromDraftSchema } from '@/entities/rx';
import { useZodForm } from '@/shared/lib/forms';

/** Zod safe parse success result. */
export type BuildRequestSuccess = { success: true; data: RxRequest };
/** Zod safe parse error result. */
export type BuildRequestError = { success: false; error: ZodError<RxDraft> };
/** Zod safe parse result for buildRequest - properly typed for error handling. */
export type BuildRequestResult = BuildRequestSuccess | BuildRequestError;

export function useRxWizardForm() {
  const { form, validateDraft } = useZodForm<RxDraft>({
    schema: rxDraftSchema,
    defaultValues: createEmptyRxDraft('coded'),
  });

  const resetForKind = useCallback(
    (kind: RxKind) => {
      // Passing values to reset() also updates the default values (desired when switching kind).
      form.reset(createEmptyRxDraft(kind));
      // Ensure the discriminant is set deterministically. In some TanStack Form states,
      // resetting cross-variant drafts can leave the previous discriminant value behind.
      form.setFieldValue('kind', kind);
    },
    [form]
  );

  const buildRequest = useCallback((): BuildRequestResult => {
    return rxRequestFromDraftSchema.safeParse(form.state.values) as BuildRequestResult;
  }, [form]);

  return { form, resetForKind, validateDraft, buildRequest };
}
